import { t } from "i18next";
import React, { useMemo, useState } from "react";
import DaumPostcodeEmbed, { type Address } from "react-daum-postcode";
import { type SubmitHandler, useForm } from "react-hook-form";

import type { MailingAddress, MailingAddressInput } from "@repo/shopifyV2/types/storefront.types";
import {
  addressShortToFull,
  addressToEnglish,
  addressToFullKorean,
  daumAddress,
  resolveLegacyKoreaProvince,
} from "@repo/shopifyV2/utils/address";
import { formatPhone } from "@repo/shopifyV2/utils/format";
import { validatePhone } from "@repo/shopifyV2/utils/regex";

import Button from "@atoms/Button";
import Input from "@atoms/Input";

import useFormErrorHandler from "@hooks/useFormErrorHandler";

import useCustomer from "@queries/useCustomer";

import { useLayoutStore } from "@store/layout";

interface IProps {
  address?: MailingAddress;
  isDefaultAddress?: boolean;
  onCompletedMutation?: (selectedAddressId?: string) => void | Promise<void>;
  onClickCancel?: () => void;
}

export default function AddressForm({ address, isDefaultAddress = false, onClickCancel, onCompletedMutation }: IProps) {
  const { openModal } = useLayoutStore();

  const { createAddress, createAddressLoading, editAddress, editAddressLoading, setDefaultAddress, setDefaultAddressLoading } =
    useCustomer();

  const [checkedDefaultAddress, setCheckedDefaultAddress] = useState<boolean>(false);

  const submitType = useMemo(() => (address?.id ? "edit" : "create"), [address]);

  const defaultValues = useMemo(
    () =>
      address
        ? {
            address1: address.address1,
            address2: address.address2,
            firstName: address.firstName,
            lastName: address.lastName,
            phone: address.phone,
            province: addressToFullKorean(address?.province),
            zip: address.zip,
          }
        : undefined,
    [address],
  );

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    getValues,
    formState: { errors },
  } = useForm<MailingAddressInput>({ defaultValues });

  const { onCommonError, displayError } = useFormErrorHandler({ values: getValues(), errors });

  const onCompleteFindPostalCode = (data: Address) => {
    setValue("address1", daumAddress(data), { shouldValidate: true });
    setValue("province", addressShortToFull(data.sido) || data.sido, { shouldValidate: true }); // 매칭 안되면 sido 그대로 표시
    setValue("zip", data.zonecode, { shouldValidate: true });
    setFocus("address2");
    openModal(null);
  };

  const handleFindPostalCode = () => {
    openModal({
      title: "Find Address",
      content: <DaumPostcodeEmbed onComplete={onCompleteFindPostalCode} style={{ overflow: "hidden", height: "395px" }} />,
      isHideCancelButton: true,
    });
  };

  const onSubmit: SubmitHandler<MailingAddressInput> = async (formData, event) => {
    if (!event) return;
    event?.preventDefault();

    if (createAddressLoading || editAddressLoading) return;

    const legacyProvince = resolveLegacyKoreaProvince(String(formData.zip));

    const _address = {
      ...formData,
      province: legacyProvince ? legacyProvince.en : addressToEnglish(formData.province),
      country: "South Korea",
      city: "-",
      phone: formatPhone(String(formData.phone)),
    };

    if (submitType === "create") {
      await createAddress({ address: _address }).then(async (createData) => {
        const checked = checkedDefaultAddress ?? false;
        const newAddressId = createData?.customerAddressCreate?.customerAddress?.id || "";
        if (checked) {
          await setDefaultAddress({ addressId: newAddressId });
        }

        await onCompletedMutation?.(newAddressId);
        onClickCancel?.();
      });

      return;
    }

    if (!address?.id) return;
    await editAddress({ address: _address, id: address.id }).then(async (editData) => {
      const checked = checkedDefaultAddress ?? false;
      if (checked) {
        await setDefaultAddress({ addressId: editData?.customerAddressUpdate?.customerAddress?.id || "" });
      }

      await onCompletedMutation?.();
      onClickCancel?.();
    });
  };

  return (
    <form className="flex flex-col" onSubmit={handleSubmit(onSubmit, onCommonError)}>
      <div className="col-span-8 flex flex-1 cursor-pointer flex-col gap-2.5">
        <div className="flex gap-2.5">
          <Input
            {...register("lastName", {
              required: { value: true, message: t("error.necessary_lastName") },
            })}
            {...displayError("lastName")}
            className="w-full"
            id="lastName"
            label={t("last_name")}
            placeholder={t("last_name")}
          />
          <Input
            {...register("firstName", {
              required: { value: true, message: t("error.necessary_firstName") },
            })}
            {...displayError("firstName")}
            className="w-full"
            id="firstName"
            label={t("first_name")}
            placeholder={t("first_name")}
          />
        </div>

        <div className="flex items-end gap-1.5">
          <Input
            {...register("zip", {
              required: { value: true, message: t("error.necessary_zip") },
            })}
            {...displayError("zip")}
            className="w-full cursor-pointer"
            id="zip"
            label={t("postal_code")}
            onClick={handleFindPostalCode}
            placeholder={t("postal_code")}
            readOnly
          />
          <Button
            as="square"
            onClick={() => {
              handleFindPostalCode();
            }}
            variant="secondary"
          >
            {t("search_address")}
          </Button>
        </div>
        <Input
          {...register("province", {
            required: { value: true, message: t("error.necessary_province") },
          })}
          {...displayError("province")}
          className="cursor-pointer"
          label={t("province")}
          onClick={handleFindPostalCode}
          placeholder={t("province")}
          readOnly
        />
        <Input
          {...register("address1", {
            required: { value: true, message: t("error.necessary_address1") },
          })}
          {...displayError("address1")}
          className="cursor-pointer"
          label={t("address1")}
          onClick={handleFindPostalCode}
          placeholder={t("address1")}
          readOnly
        />
        <Input
          {...register("address2", {
            required: { value: true, message: t("error.necessary_address2") },
          })}
          {...displayError("address2")}
          label={t("address2")}
          placeholder={t("address2")}
        />
        <Input
          {...register("phone", {
            required: { value: true, message: t("error.necessary_phone") },
            validate(value) {
              if (!validatePhone(value)) return t("error.invalid_phone");
            },
          })}
          {...displayError("phone")}
          id="phone"
          label={t("phone_number")}
          placeholder={t("phone_number")}
          type="tel"
        />
      </div>

      <div className="pc:gap-2.5 pc:pt-4 absolute bottom-0 left-0 col-span-8 flex w-full shrink-0 cursor-pointer flex-col gap-5 bg-white p-4 pt-5">
        {!isDefaultAddress && (
          <Input
            checked={checkedDefaultAddress}
            id="defaultAddress"
            label={t("address_book.set_as_default_address")}
            name="defaultAddress"
            onChange={() => {
              setCheckedDefaultAddress((prev) => !prev);
            }}
            type="checkbox"
          />
        )}
        <Button as="square" loading={createAddressLoading || editAddressLoading || setDefaultAddressLoading} type="submit">
          {t("address_book.save")}
        </Button>
      </div>
    </form>
  );
}
